/**
 * Schema migration chain (spec §20). Someone will open a six-month-old link;
 * it must still work. Each migration upgrades a payload by exactly one version.
 *
 * To add a version: bump CURRENT_SCHEMA_VERSION in types.ts, append a migration
 * function here keyed by the FROM version, and add a fixture in the tests.
 */
import { CURRENT_SCHEMA_VERSION, type Plan } from './types';

type AnyPlan = Record<string, unknown>;

/** migrations[n] upgrades a v(n) payload to v(n+1). */
const migrations: Record<number, (p: AnyPlan) => AnyPlan> = {
  // Example of the shape future migrations take. v0 -> v1 is a no-op stub kept
  // so the chain has a documented starting point.
  0: (p) => ({ ...p, schemaVersion: 1 }),
};

export interface MigrationResult {
  plan: Plan;
  migratedFrom: number;
  migratedTo: number;
}

export function migratePlan(raw: AnyPlan): MigrationResult {
  const from = typeof raw.schemaVersion === 'number' ? raw.schemaVersion : 0;
  if (from > CURRENT_SCHEMA_VERSION) {
    throw new Error(
      `This plan was saved by a newer version of the planner (schema v${from}). ` +
        `Please update the app to open it.`,
    );
  }

  let current: AnyPlan = raw;
  for (let v = from; v < CURRENT_SCHEMA_VERSION; v++) {
    const migrate = migrations[v];
    if (!migrate) {
      throw new Error(`Missing migration from schema v${v}. Cannot safely upgrade this plan.`);
    }
    current = migrate(current);
  }
  current.schemaVersion = CURRENT_SCHEMA_VERSION;

  return { plan: current as unknown as Plan, migratedFrom: from, migratedTo: CURRENT_SCHEMA_VERSION };
}
